import { zodResolver } from '@hookform/resolvers/zod';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import type { Theme } from '@mui/material/styles';
import {
  alpha,
  Box,
  GlassAlert,
  GlassButton,
  GlassTextField,
  iconSizes,
  Link,
  ShimmerLoader,
  Typography,
  useI18n,
  useTerminology,
} from '@ops-matrix/design-system-ui';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { env } from '@/config/env';
import { auth } from '../config/firebase';
import { isValidRedirectUrl } from '../hooks/useRedirect';
import { useTheme } from '../theme/ThemeProvider';

const DEFAULT_PORTAL_REDIRECTS: Record<string, string> = {
  admin: env.VITE_ADMIN_PORTAL_URL,
  business: env.VITE_BUSINESS_PORTAL_URL,
  consumer: env.VITE_CONSUMER_PORTAL_URL,
};

/** Seconds the resend button stays disabled after a successful send. */
const RESEND_COOLDOWN_SECONDS = 60;

/** Format `seconds` as `m:ss` (e.g. 47 → "0:47", 65 → "1:05"). */
function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.trunc(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

/**
 * MagicLinkPage — v3 (Issue #1749 follow-up).
 *
 * Two surfaces share one route:
 *
 * 1. **Initial entry** — user types their email, we POST to
 *    `/consumers/auth/magic-link/send`, save the email for the click-back
 *    verification, and transition to the sent state. Form uses the same
 *    `<GlassTextField>` + primary-button pattern as LoginPage so the v3
 *    auth surfaces feel consistent.
 *
 * 2. **Sent confirmation** — a big mint-tinted Astor-style card with mail
 *    icon, the destination email rendered in mono mint, expiry note, and a
 *    "Resend in m:ss" ghost button that re-fires the POST once the cooldown
 *    elapses. Below the card, a glass-panel info note explains why magic
 *    links are stronger than password+2FA.
 *
 * The page itself does NOT render the marketing left-pane — that's owned by
 * `AuthLayout`. Same Outlet pattern as LoginPage / RegisterPage /
 * ForgotPasswordPage.
 *
 * Verification path (user clicks the magic link in their email and lands
 * back here) is unchanged from v2: `isSignInWithEmailLink` → token exchange
 * via `/auth/exchange` → redirect with `?token=`.
 */
export default function MagicLinkPage() {
  const { t } = useI18n();
  const { term } = useTerminology();
  const platformName = term('navigation.platformName');
  const [searchParams] = useSearchParams();
  const { portalType, redirectUrl, tenantId, businessId: resolvedBusinessId } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  // The /consumers/auth/magic-link/send endpoint requires a string businessId
  // (Zod rejects null with `expected: "string"`, returning 500). Sources, in
  // priority order:
  //   1. ?tenant=<id> URL param (legacy explicit). Captured as `tenantId`.
  //   2. ?businessId= / ?b= URL param OR resolved-from-redirect-domain.
  //      ThemeProvider runs /api/v1/website/resolve-domain on the redirect's
  //      hostname (e.g. portal.coastal-funding.com → resolves to Coastal
  //      Funding's businessId), so a user landing here from a custom-domain
  //      portal automatically inherits the right businessId without a
  //      ?tenant= param in the URL.
  // If neither resolves, the magic-link button below is disabled because the
  // server cannot accept a null/missing value.
  const effectiveBusinessId = tenantId ?? resolvedBusinessId ?? null;
  // Magic-link is the BORROWER (consumer) flow. The LoginPage hides the
  // "Sign in with magic link" button when `?portal=business` or
  // `?portal=admin`, but the URL is still reachable directly. If a business
  // user lands here anyway, the BORROWER hardcode below would funnel them
  // into the consumer namespace — we have no way to recover from that.
  // Surfacing a clearer error than the post-submit "Failed to send" toast
  // is enough; we keep the button-hide as the primary defense.

  const magicLinkSchema = z.object({
    email: z.string().email(t('validation.email')),
  });

  type MagicLinkFormData = z.infer<typeof magicLinkSchema>;

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
  });

  // Resend countdown: start one interval when the cooldown begins, clear it
  // when it ends. Keying on the boolean `isCoolingDown` (rather than the raw
  // `resendSeconds` value) means the effect only fires on edges (0→positive,
  // positive→0) instead of every tick — no per-second teardown/recreate.
  const isCoolingDown = resendSeconds > 0;
  useEffect(() => {
    if (!isCoolingDown) return;
    const id = setInterval(() => {
      setResendSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [isCoolingDown]);

  // Check if user is arriving from a magic link (click-back verification).
  useEffect(() => {
    const verifyMagicLink = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        setIsVerifying(true);
        const savedEmail = localStorage.getItem('emailForSignIn');

        if (!savedEmail) {
          setError(t('auth.magicLink.enterEmail'));
          setIsVerifying(false);
          return;
        }

        try {
          const userCredential = await signInWithEmailLink(auth, savedEmail, window.location.href);
          localStorage.removeItem('emailForSignIn');

          const idToken = await userCredential.user.getIdToken();

          // Exchange token and redirect
          const response = await fetch(`${env.VITE_API_URL}/auth/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firebaseIdToken: idToken }),
          });

          if (!response.ok) {
            throw new Error('Failed to exchange token');
          }

          const { token } = await response.json();

          if (redirectUrl && isValidRedirectUrl(redirectUrl)) {
            const separator = redirectUrl.includes('?') ? '&' : '?';
            window.location.href = `${redirectUrl}${separator}token=${token}`;
          } else {
            const destination = DEFAULT_PORTAL_REDIRECTS[portalType ?? 'business'] ?? DEFAULT_PORTAL_REDIRECTS.business;
            window.location.href = `${destination}?token=${token}`;
          }
        } catch (err) {
          setError(t('auth.magicLink.invalidLink'));
          console.error('Magic link verification failed:', err);
        } finally {
          setIsVerifying(false);
        }
      }
    };

    verifyMagicLink();
  }, [portalType, redirectUrl, t]);

  /**
   * POST the magic-link send request. Shared between the initial form
   * submission and the "Resend now" button in the sent state.
   */
  const sendMagicLink = useCallback(
    async (email: string): Promise<boolean> => {
      setError(null);

      // Refuse to POST when we have nothing valid to send. The server's Zod
      // schema is `businessId: z.string().optional()` — it accepts undefined
      // (omitted) but NOT null (`expected: "string"` → 500). Showing a clear
      // error here is better than the opaque "Failed to send magic link" the
      // server response would surface.
      if (!effectiveBusinessId) {
        setError(t('auth.magicLink.businessIdMissing'));
        console.warn('[MagicLink] No businessId resolved (no ?tenant= URL param, no resolved-from-domain)');
        return false;
      }

      try {
        const response = await fetch(`${env.VITE_API_URL}/consumers/auth/magic-link/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            consumerType: 'BORROWER',
            businessId: effectiveBusinessId,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send magic link');
        }

        localStorage.setItem('emailForSignIn', email);
        return true;
      } catch (err) {
        setError(t('auth.magicLink.sendError'));
        console.error('Magic link send failed:', err);
        return false;
      }
    },
    [effectiveBusinessId, t],
  );

  const onSubmit = async (data: MagicLinkFormData) => {
    setIsLoading(true);
    const ok = await sendMagicLink(data.email);
    if (ok) {
      setSentEmail(data.email);
      setEmailSent(true);
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
    }
    setIsLoading(false);
  };

  const handleResend = async () => {
    const email = sentEmail ?? getValues('email');
    if (!email) return;
    setIsLoading(true);
    const ok = await sendMagicLink(email);
    if (ok) setResendSeconds(RESEND_COOLDOWN_SECONDS);
    setIsLoading(false);
  };

  const handleGoBack = () => {
    setEmailSent(false);
    setSentEmail(null);
    setError(null);
    setResendSeconds(0);
  };

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  // ============================================================
  // VERIFYING (user clicked link in email and just landed back)
  // ============================================================
  if (isVerifying) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          minHeight: 240,
          py: 4,
        }}
      >
        <ShimmerLoader variant="circular" width={40} height={40} />
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('auth.magicLink.verifying')}
        </Typography>
      </Box>
    );
  }

  // ============================================================
  // SENT CONFIRMATION (v3 design)
  // ============================================================
  if (emailSent) {
    const canResend = resendSeconds <= 0 && !isLoading;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Page header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
            {t('auth.magicLink.sentTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {t('auth.magicLink.sentSubtitle')}
          </Typography>
        </Box>

        <GlassAlert severity="error" visible={!!error} sx={{ mb: 2 }}>
          {error}
        </GlassAlert>

        {/* Mint-tinted Astor-style confirmation card */}
        <Box
          sx={(theme: Theme) => ({
            bgcolor: alpha(theme.palette.success.main, 0.06),
            border: '1px solid',
            borderColor: alpha(theme.palette.success.main, 0.25),
            borderRadius: 2,
            p: 2.75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mb: 2.75,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.success.main, 0.06)} inset`,
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              width: 56,
              height: 56,
              borderRadius: 1.75,
              bgcolor: alpha(theme.palette.success.main, 0.15),
              border: '1px solid',
              borderColor: alpha(theme.palette.success.main, 0.3),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.75,
            })}
          >
            <MailOutlineIcon sx={{ fontSize: iconSizes.lg, color: 'success.main' }} />
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
            {t('auth.magicLink.sentToLabel')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
              fontWeight: 600,
              mb: 1.75,
              wordBreak: 'break-all',
            }}
          >
            {sentEmail}
          </Typography>

          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55, display: 'block', mb: 1.75 }}>
            {t('auth.magicLink.expiryPrefix')}{' '}
            <Box
              component="span"
              sx={{
                color: 'text.primary',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                fontWeight: 600,
              }}
            >
              {t('auth.magicLink.expiryDuration')}
            </Box>
            .
            <br />
            {t('auth.magicLink.expirySecondary')}
          </Typography>

          <GlassButton
            variant="ghost"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: iconSizes.xs }} />}
            onClick={handleResend}
            disabled={!canResend}
          >
            {resendSeconds > 0
              ? t('auth.magicLink.resendIn', { seconds: formatCountdown(resendSeconds) })
              : t('auth.magicLink.resendNow')}
          </GlassButton>
        </Box>

        {/* "WHY MAGIC LINKS?" educational note — platform-product copy
            (kept as i18n with platform defaults; not a tenant-specific
            string, so no white-label substitution needed). */}
        <Box
          sx={(theme: Theme) => ({
            p: 1.75,
            bgcolor: alpha(theme.palette.background.default, 0.6),
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <ShieldOutlinedIcon sx={{ fontSize: iconSizes['2xs'], color: 'success.main' }} />
            <Typography
              variant="overline"
              sx={{
                color: 'success.main',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                fontWeight: 600,
                letterSpacing: '0.08em',
              }}
            >
              {t('auth.magicLink.whyHeading')}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55, display: 'block' }}>
            {t('auth.magicLink.whyBody')}
          </Typography>
        </Box>

        {/* Footer: "Wrong email? Go back" */}
        <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('auth.magicLink.wrongEmailPrompt')}{' '}
            <Link
              component="button"
              type="button"
              onClick={handleGoBack}
              sx={{ color: 'success.main', fontWeight: 600 }}
            >
              {t('auth.magicLink.goBack')} →
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  }

  // ============================================================
  // INITIAL ENTRY (email form)
  // ============================================================
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
          {t('auth.magicLink.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {t('auth.magicLink.subtitle', { platformName })}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 2 }}>
        {error}
      </GlassAlert>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column' }}>
        <GlassTextField
          {...register('email')}
          label={t('common.email')}
          type="email"
          autoComplete="email"
          autoFocus
          disabled={isLoading}
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          sx={{ mb: 2.5 }}
        />

        <GlassButton
          type="submit"
          variant="contained"
          color="success"
          fullWidth
          size="large"
          disabled={isLoading}
          startIcon={!isLoading ? <LinkIcon /> : undefined}
        >
          {isLoading ? <ShimmerLoader variant="circular" width={24} height={24} /> : t('auth.magicLink.sendButton')}
        </GlassButton>
      </Box>

      {/* Footer: back to sign-in */}
      <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <Link component={RouterLink} to={`/login${queryString}`} sx={{ color: 'success.main', fontWeight: 600 }}>
            ← {t('auth.magicLink.backToSignIn')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
